import {
  Box,
  Button,
  Hint,
  SearchField,
  Spinner,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { RoleCompetencyFragment } from 'generated/resources/graphql'

import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useRoleCompetencies } from 'modules/job_catalog/hooks/useRoleCompetencies'
import useUnassignCompetencyFromRole from 'modules/job_catalog/hooks/useUnassignCompetencyFromRole'
import policyAuthorizations, {
  PolicyTarget,
} from 'modules/permissions/store/collections/policyAuthorizations'

import Row from './Row'

interface Props {
  roleId: number
}

const filteredBySearchBox = createComputed((roleCompetencies: RoleCompetencyFragment[], search) =>
  roleCompetencies.filter((competency) => {
    return matchText(search, competency.name || '')
  })
)

const RoleCompetenciesTable: React.FC<Props> = ({ roleId }) => {
  const rolePolicyTarget: PolicyTarget = { id: roleId, policyTargetClass: '::JobCatalog::Role' }
  const [roleCompetencies, isLoading, refetch] = useRoleCompetencies(roleId)
  const { navigation } = useNavigation()
  const unassignCompetencyFromRole = useUnassignCompetencyFromRole()
  const [hideRoleCompetenciesSuggestionsTip, setHideRoleCompetenciesSuggestionsTip] =
    useLocalStorage<boolean>(LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideRoleCompetenciesSuggestionsTip)
  const canManageCompetencies = policyAuthorizations.for('competencies.manage_v2')
  const canEditJob = policyAuthorizations.for('job_catalog.edit_job', rolePolicyTarget)
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { loading: fetchingPolicies } = useAsyncData(
    async () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('competencies.manage_v2', []),
        policyAuthorizations.authorizePolicies('job_catalog.edit_job', [rolePolicyTarget]),
      ]),
    []
  )

  const filteredCompetencies = filteredBySearchBox(roleCompetencies || [], debouncedSearch).get()

  const rows = filteredCompetencies.map((competency) => {
    const handleUnassign = async () => {
      track(trackingEvents.jobCatalog.jobRolePageCompetencyUnassignSubmit)
      await unassignCompetencyFromRole(roleId, competency.id)
      refetch()
    }

    return (
      <Row
        key={competency.id}
        competency={competency}
        onUnassign={handleUnassign}
        canEditJob={canEditJob}
      />
    )
  })

  const handleHintFinish = () => {
    setHideRoleCompetenciesSuggestionsTip(true)
  }

  const handleHintClose = () => {
    track(trackingEvents.jobCatalog.createAiCompetenciesHintClose)
  }

  if (isLoading || fetchingPolicies)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Table
      options={() => (
        <SearchField
          variant="big"
          search={search}
          onSearch={onSearch}
          placeholder={i18n.t('common.search')}
        />
      )}
    >
      <Table.Head>
        <Table.Row>
          <Table.HeadCell colSpan={3}>
            {i18n.t('job_catalog.competencies.competency_name')}
          </Table.HeadCell>
          <Table.HeadCell colSpan={6}>{i18n.t('common.description')}</Table.HeadCell>
          <Table.HeadCell colSpan={2}>{i18n.t('job_catalog.competencies.audience')}</Table.HeadCell>
          <Table.HeadCell colSpan={1} />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {rows?.length ? (
          rows
        ) : (
          <Hint.Popover.Provider
            steps={1}
            isHidden={!!hideRoleCompetenciesSuggestionsTip}
            onFinish={handleHintFinish}
            onClose={handleHintClose}
          >
            <Table.Row>
              <Table.Cell colSpan={10}>
                <Box flexDirection="column" alignItems="center" gap="s16" paddingY="s96">
                  <Box gap="s4" alignItems="center">
                    <Text.Normal color="grey1000" weight="semibold">
                      {i18n.t('job_catalog.competencies.competencies_table_empty_title')}
                    </Text.Normal>
                    {canManageCompetencies && canEditJob && (
                      <Text.Normal>
                        {i18n.t('job_catalog.competencies.competencies_table_empty_description')}
                      </Text.Normal>
                    )}
                  </Box>
                  {canManageCompetencies && canEditJob && (
                    <Hint.Popover
                      step={1}
                      title={i18n.t('job_catalog.competencies.ai_autofill_hint_title')}
                      description={i18n.t('job_catalog.competencies.ai_autofill_hint_description')}
                      actionLabel={i18n.t('common.close')}
                      position="left top"
                    >
                      <Button.Router
                        onClick={() => track(trackingEvents.jobCatalog.createAiCompetenciesStart)}
                        to={navigation.employees.jobCatalog.role.competencies.createCompetenciesFromSuggestions.fullPath(
                          { id: roleId }
                        )}
                      >
                        {i18n.t('job_catalog.competencies.competencies_table_empty_action')}
                      </Button.Router>
                    </Hint.Popover>
                  )}
                </Box>
              </Table.Cell>
            </Table.Row>
          </Hint.Popover.Provider>
        )}
      </Table.Body>
    </Table>
  )
}

export default RoleCompetenciesTable
